import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useStorefrontData } from '../context/StorefrontDataContext';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  type?: 'website' | 'product' | 'article';
  schema?: Record<string, any> | Record<string, any>[];
}

export default function SEO({ 
  title, 
  description, 
  keywords,
  ogImage, 
  canonical,
  type = 'website',
  schema
}: SEOProps) {
  const location = useLocation();
  
  // Safe extraction of storefront CMS data
  let globalSiteTitle = "SECERA | Effortless Occasion Wear";
  let globalDescription = "Tampil siap bukan soal waktu yang panjang. SECERA menghadirkan koleksi effortless occasion wear premium—dari selendang instan hingga setelan busana modular.";
  let globalKeywords = "";

  try {
    const storefront = useStorefrontData();
    if (storefront?.cms?.global?.siteTitle) {
      globalSiteTitle = storefront.cms.global.siteTitle;
    }
    if (storefront?.cms?.global?.seoDescription) {
      globalDescription = storefront.cms.global.seoDescription;
    }
    if (storefront?.cms?.global?.seoKeywords) {
      globalKeywords = storefront.cms.global.seoKeywords;
    }
  } catch {
    // Fallback if rendered outside StorefrontDataProvider
  }

  // Derive brand name from global siteTitle (e.g. "SECERA | Menghiasi Hari Spesialmu." -> "SECERA")
  const brandName = globalSiteTitle.includes('|') 
    ? globalSiteTitle.split('|')[0].trim() 
    : (globalSiteTitle.trim() || "SECERA");

  // Determine fullTitle
  let fullTitle = globalSiteTitle;
  if (title) {
    const trimmedTitle = title.trim();
    if (
      trimmedTitle === globalSiteTitle ||
      trimmedTitle.toLowerCase() === brandName.toLowerCase() ||
      trimmedTitle.toLowerCase().includes(brandName.toLowerCase())
    ) {
      fullTitle = trimmedTitle;
    } else {
      fullTitle = `${trimmedTitle} | ${brandName}`;
    }
  }

  const finalDescription = description || globalDescription;
  const finalKeywords = keywords || globalKeywords;
  const baseUrl = "https://secera.id";
  const currentUrl = `${baseUrl}${location.pathname}${location.search}`;
  const finalCanonical = canonical || currentUrl;
  
  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={finalDescription} />
        {finalKeywords && <meta name="keywords" content={finalKeywords} />}
        <link rel="canonical" href={finalCanonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={type} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={finalDescription} />
        <meta property="og:url" content={currentUrl} />
        {ogImage && <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={finalDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />}
      </Helmet>

      {/* Structured Data (Schema Markup) rendered directly in page body to bypass Helmet SPA sync limits */}
      {schema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      )}
    </>
  );
}
